import type { Site } from '../../schemas/validation';
import { gettingStartedApi, type GettingStartedProgress } from '../../api/modules/gettingStarted';

/** Same-tab signal so the host can re-derive after progress changes. */
export const GETTING_STARTED_CHANGE_EVENT = 'cp-getting-started-change';

export type GettingStartedStepId = 'create-site' | 'open-site' | 'edit-page' | 'publish-site';

export interface GettingStartedHelpLink {
  /** Preferred guide slug under /help/:slug when published in CMS */
  slug?: string;
  category: string;
}

export const HELP_LINKS: Record<GettingStartedStepId, GettingStartedHelpLink> = {
  'create-site': { slug: 'create-a-website', category: 'getting-started' },
  'open-site': { slug: 'edit-your-site', category: 'getting-started' },
  'edit-page': { slug: 'edit-a-page', category: 'getting-started' },
  'publish-site': { slug: 'publish-your-site', category: 'getting-started' },
};

export interface GettingStartedStep {
  id: GettingStartedStepId;
  title: string;
  description: string;
  complete: boolean;
  helpHref: string;
}

export type GettingStartedSite = Pick<Site, 'id' | 'isPublished' | 'homePageId'>;

export interface GettingStartedFlags {
  dismissed?: boolean;
  openedSite?: boolean;
  editedPage?: boolean;
}

export interface GettingStartedState {
  steps: GettingStartedStep[];
  completedCount: number;
  allComplete: boolean;
  /** True until any site is published — FAB / panel host should render */
  shouldShow: boolean;
  shouldAutoOpen: boolean;
  hasPublishedSite: boolean;
  firstSiteId: string | null;
  /** Home page (or known first page) for the edit-page CTA */
  firstPageId: string | null;
  /** First incomplete step — primary CTA target */
  nextStepId: GettingStartedStepId | null;
}

function notifyGettingStartedChange(): void {
  try {
    window.dispatchEvent(new Event(GETTING_STARTED_CHANGE_EVENT));
  } catch {
    /* ignore non-browser */
  }
}

async function patchAndNotify(patch: {
  openedSite?: true;
  editedPage?: true;
  dismissed?: true;
}): Promise<void> {
  try {
    await gettingStartedApi.patch(patch);
    notifyGettingStartedChange();
  } catch (error) {
    console.error('Failed to update getting started progress:', error);
  }
}

export function markOpenedSite(): void {
  void patchAndNotify({ openedSite: true });
}

export function markEditedPage(): void {
  void patchAndNotify({ editedPage: true });
}

export function dismissGettingStarted(): void {
  void patchAndNotify({ dismissed: true });
}

/** Build help href: prefer guide slug path; category query is the reliable fallback. */
export function getHelpHref(link: GettingStartedHelpLink): string {
  if (link.slug) {
    return `/help/${link.slug}`;
  }
  return `/help?category=${encodeURIComponent(link.category)}`;
}

export function getCategoryHelpHref(category = 'getting-started'): string {
  return `/help?category=${encodeURIComponent(category)}`;
}

export function progressToFlags(progress: GettingStartedProgress): GettingStartedFlags {
  return {
    openedSite: progress.openedSite,
    editedPage: progress.editedPage,
    dismissed: progress.dismissed,
  };
}

export function deriveGettingStartedState(
  sites: GettingStartedSite[],
  flags: GettingStartedFlags = {},
  /** Override when homePageId is missing but a page was resolved via API */
  firstPageIdOverride?: string | null
): GettingStartedState {
  const dismissed = flags.dismissed ?? false;
  const openedSite = flags.openedSite ?? false;
  const editedPage = flags.editedPage ?? false;

  const hasSite = sites.length > 0;
  const hasPublishedSite = sites.some(s => s.isPublished);
  const firstSite = hasSite ? sites[0] : null;
  const firstSiteId = firstSite?.id ?? null;
  const firstPageId =
    firstPageIdOverride !== undefined ? firstPageIdOverride : (firstSite?.homePageId ?? null);

  const steps: GettingStartedStep[] = [
    {
      id: 'create-site',
      title: 'Create a website',
      description: 'Start from a blank site or a template.',
      complete: hasSite,
      helpHref: getHelpHref(HELP_LINKS['create-site']),
    },
    {
      id: 'open-site',
      title: 'Open your website',
      description: 'Go to the site manager to see pages and settings.',
      complete: openedSite,
      helpHref: getHelpHref(HELP_LINKS['open-site']),
    },
    {
      id: 'edit-page',
      title: 'Edit a page',
      description: 'Open the page editor and customize your content.',
      complete: editedPage,
      helpHref: getHelpHref(HELP_LINKS['edit-page']),
    },
    {
      id: 'publish-site',
      title: 'Publish your site',
      description: 'Make your site live so visitors can see it.',
      complete: hasPublishedSite,
      helpHref: getHelpHref(HELP_LINKS['publish-site']),
    },
  ];

  const completedCount = steps.filter(s => s.complete).length;
  const allComplete = completedCount === steps.length;
  const shouldShow = !hasPublishedSite;
  const shouldAutoOpen = !hasSite && !dismissed && shouldShow;
  const nextStepId = steps.find(s => !s.complete)?.id ?? null;

  return {
    steps,
    completedCount,
    allComplete,
    shouldShow,
    shouldAutoOpen,
    hasPublishedSite,
    firstSiteId,
    firstPageId,
    nextStepId,
  };
}
